/** @jsxImportSource @emotion/react */
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import _ from 'lodash';
import { Button, Checkbox } from '@mui/material';
import { CellMaker } from '@grapecity/wijmo.grid.cellmaker';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import { IconButton } from 'components/buttons/IconSVG';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchButtonWrap,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import CustomDialog from 'components/modals/common/CustomDialog';
import CustomGrid from 'components/grids/CustomGrid';
import { ControlBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { MultiComboBox } from 'components/selects/ComboBox';
import { getCommonCodeNames, getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import { getExcelFileName } from 'utils/ExcelUtil';
import { Code } from 'models/common/CommonCode';
import { useMessageBar } from 'hooks/useMessageBar';
import { SearchParam, UtMatrixRegistHistory } from 'models/ut/UtMatrixRegistHistory';
import { st } from 'components/inputs/CustomInput';
import { getUtMatrixRegistHistoryList } from 'apis/ut/UtMatrixRegistHistory';
import { CommonUtil } from 'utils/CommonUtil';

/**
 * @desc 설비별 UT Matrix 팝업
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.08.05   jihwan89       CSR E20260727-000228 Vacuum Pump 추가
 */

interface Props {
  open: boolean;
  close: () => void;
  condition?: {
    prdnProcCd?: string;
    eqclId?: string;
    dtalProcCd?: string;
    eqpMchId?: string;
    stndEqpId?: string;
  };
}

const UtMatrixRegistHistoryPoupup = ({ open, close, condition }: Props) => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const navigate = useNavigate();
  const [rowData, setRowData] = useState<UtMatrixRegistHistory[]>([]);
  const [prdnProcCode, setPrdnProcCode] = useState<Code[]>([]);
  const [eqclIdCode, setEqclIdCode] = useState<Code[]>([]);
  const [dtalProcCode, setDtalProcCode] = useState<Code[]>([]);

  const initSearchParam = {
    prdnProcCds: [],
    eqclIds: [],
    dtalProcCds: [],
    eqpMchId: '',
    stndEqpId: '',
  };
  const [searchParam, setSearchParam] = useState<SearchParam>(initSearchParam);
  const [searchState, setSearchState] = useState<any>();

  useEffect(() => {
    getCommonCodes();
  }, []);

  useEffect(() => {
    if (!_.isEmpty(condition)) {
      setSearchParam({
        prdnProcCds: [condition?.prdnProcCd] || [],
        eqclIds: [condition?.eqclId] || [],
        stndEqpId: condition?.stndEqpId || '',
        dtalProcCds: [] || '',
        eqpMchId: '',
      });
      if (condition?.prdnProcCd && condition?.eqclId) {
        setSearchState(true);
      }
    }
  }, [condition?.prdnProcCd, condition?.eqclId]);

  useEffect(() => {
    if (searchState) {
      handleSearch();
      setSearchState(false);
    }
  }, [searchState]);

  const getCommonCodes = async () => {
    const prdnProcCd = await getCommonCodeNamesCondition({
      optValCtn1: 'Y',
      cmnGrCd: 'PRDN_PROC_CD',
    });
    const eqclIdCd = await getCommonCodeNamesCondition({
      optValCtn1: 'Y',
      cmnGrCd: 'EQCL_ID',
    });
    const dtalProcCd = await getCommonCodeNames('DTAL_PROC_CD');
    setPrdnProcCode(prdnProcCd);
    setEqclIdCode(eqclIdCd);
    setDtalProcCode(dtalProcCd);
  };

  const handleOnChange = (name, value) => {
    setSearchParam({ ...searchParam, [name]: value });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleExportExcel = () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(gridRef.current, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
    });
    book.sheets[0].name = t('ut.label.표준설비코드 List', '표준설비코드 List');
    book.saveAsync(getExcelFileName(t('ut.label.표준설비코드 List', '표준설비코드 List')));
  };

  /**
   * 조회 이벤트 처리
   */
  const handleSearch = async () => {
    getUtMatrixRegistHistoryList(searchParam).then((result: UtMatrixRegistHistory[]) => {
      if (result !== null) {
        setRowData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        width: 40,
        align: 'center',
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'prdnProcCdNm',
        header: String(t('ut.label.공정', '공정')),
        width: 100,
        align: 'center',
      },
      {
        binding: 'eqclIdNm',
        header: String(t('ut.label.설비군', '설비군')),
        width: 100,
      },
      {
        binding: 'stndEqpId',
        header: String(t('ut.label.표준설비코드', '표준설비코드')),
        width: 110,
        align: 'center',
      },
      {
        binding: 'dtalProcCdNm',
        header: String(t('ut.label.세부공정', '세부공정')),
        width: 110,
      },
      {
        binding: 'eqpMainNm',
        header: String(t('ut.label.Main', 'Main')),
        width: 120,
      },
      {
        binding: 'eqpMchNm',
        header: String(t('ut.label.Machine', 'Machine')),
        width: 120,
      },
      {
        binding: 'bildPlntCdNm',
        header: String(t('ut.label.공장', '공장')),
        width: 100,
        align: 'center',
      },
      {
        binding: 'utmId',
        header: String(t('ut.label.UT Matrix 번호', 'UT Matrix 번호')),
        width: 130,
        visible: false,
      },
      {
        binding: 'utmNo',
        header: String(t('ut.label.UT Matrix 번호', 'UT Matrix 번호')),
        width: 109,
        align: 'center',
        cellTemplate: CellMaker.makeLink({
          text: '${item.utmNo}',
          click: (e, ctx) => {
            fnSearchUtmNm(ctx.item);
          },
        }),
      },
      {
        binding: 'utmNm',
        header: String(t('ut.label.UT Matrix 명', 'UT Matrix 명')),
        width: 250,
        align: 'left',
      },
      {
        binding: 'verNo',
        header: String(t('ut.label.버전', '버전')),
        width: 50,
        align: 'center',
      },
      {
        binding: 'prdnLnNm',
        header: String(t('ut.label.Line', 'Line')),
        width: 60,
        align: 'center',
      },
      {
        binding: 'prdnPrlLnNm',
        header: String(t('ut.label.Sub Line', 'Sub Line')),
        width: 60,
        align: 'center',
      },
      {
        header: String(t('ut.label.설비 Qty', '설비 Qty')),
        columns: [
          {
            binding: 'machEqpQty',
            header: String(t('ut.label.Mech', 'Mech')),
            width: 80,
            align: 'right',
            cellTemplate: (params) => Number(params.value || 0).toLocaleString(),
          },
          {
            binding: 'elpwEqpQty',
            header: String(t('ut.label.Elec', 'Elec')),
            width: 80,
            align: 'right',
            cellTemplate: (params) => Number(params.value || 0).toLocaleString(),
          },
        ],
      },
      {
        header: String(t('ut.label.Library', 'Library')),
        columns: [
          {
            binding: 'utmLibId',
            header: String(t('ut.label.ID', 'ID')),
            width: 100,
          },
          {
            binding: 'utmLibVerNo',
            header: String(t('ut.label.Version', 'Version')),
            width: 100,
          },
        ],
      },
      {
        binding: 'insTpCdNm',
        header: String(t('ut.label.작성구분', '작성구분')),
        width: 100,
        align: 'center',
      },
      {
        header: String(t('ut.label.Voltage', 'Voltage')),
        columns: [
          {
            binding: 'vltgNvl',
            // header: String(t('ut.label.Voltage[V]', 'Voltage[V]')),
            header: '[V]',
            width: 60,
            align: 'center',
          },
        ],
      },
      {
        header: String(t('ut.label.Phase', 'Phase')),
        columns: [
          {
            binding: 'elpwPhasCd',
            header: '[Φ]',
            width: 60,
            align: 'center',
          },
        ],
      },
      {
        header: String(t('ut.label.Wires', 'Wires')),
        columns: [
          {
            binding: 'ctwTpCd',
            header: '[EA]',
            width: 60,
            align: 'center',
          },
        ],
      },
      {
        header: String(t('ut.label.Current', 'Current')),
        columns: [
          {
            binding: 'od2EcNvl',
            header: String(t('ut.label.입력값 [A]', '입력값 [A]')),
            width: 97,
            align: 'right',
            cellTemplate: (params) => Number(params.value || 0).toLocaleString(),
          },
          {
            binding: 'od1EcNvl',
            header: String(t('ut.label.계산값 [A]', '계산값 [A]')),
            width: 97,
            align: 'right',
            cellTemplate: (params) => Number(params.value || 0).toLocaleString(),
          },
        ],
      },
      {
        header: String(t('ut.label.Breaker', 'Breaker')),
        columns: [
          {
            binding: 'brkeCapa',
            header: String(t('ut.label.입력값 [A]', '입력값 [A]')),
            width: 97,
            align: 'right',
            cellTemplate: (params) => Number(params.value || 0).toLocaleString(),
          },
          {
            binding: 'optValCtn1',
            header: String(t('ut.label.계산값 [A]', '계산값 [A]')),
            width: 97,
            align: 'right',
            cellTemplate: (params) => Number(params.value || 0).toLocaleString(),
          },
        ],
      },
      {
        header: String(t('ut.label.Capacity', 'Capacity')),
        columns: [
          {
            binding: 'elpwCapa',
            header: '[kW]',
            width: 83,
            align: 'right',
            cellTemplate: (params) => Number(params.value || 0).toLocaleString(),
          },
        ],
      },
      {
        header: String(t('ut.label.CDA', 'CDA')),
        columns: [
          {
            binding: 'cdaCspCapa',
            header: '[ℓ/min]',
            width: 95,
            align: 'right',
            cellTemplate: (params) => Number(params.value || 0).toLocaleString(),
          },
        ],
      },
      {
        header: String(t('ut.label.N2', 'N2')),
        columns: [
          {
            binding: 'nitrPeakCspCapa',
            header: '[ℓ/min]',
            width: 95,
            align: 'right',
            cellTemplate: (params) => Number(params.value || 0).toLocaleString(),
          },
        ],
      },
      {
        header: String(t('ut.label.IW', 'IW')),
        columns: [
          {
            binding: 'iwCspCapa',
            header: '[ℓ/min]',
            width: 95,
            align: 'right',
            cellTemplate: (params) => Number(params.value || 0).toLocaleString(),
          },
        ],
      },
      {
        header: String(t('ut.label.FW', 'FW')),
        columns: [
          {
            binding: 'frwtCspCapa',
            header: '[ℓ/min]',
            width: 95,
            align: 'right',
            cellTemplate: (params) => Number(params.value || 0).toLocaleString(),
          },
        ],
      },
      {
        header: String(t('ut.label.PCW', 'PCW')),
        columns: [
          {
            binding: 'coolCspCapa',
            header: '[ℓ/min]',
            width: 95,
            align: 'right',
            cellTemplate: (params) => Number(params.value || 0).toLocaleString(),
          },
        ],
      },
      {
        header: String(t('ut.label.WW', 'WW')),
        columns: [
          {
            binding: 'wwCspCapa',
            header: '[ℓ/min]',
            width: 95,
            align: 'right',
            cellTemplate: (params) => Number(params.value || 0).toLocaleString(),
          },
        ],
      },
      {
        header: String(t('ut.label.Steam', 'Steam')),
        columns: [
          {
            binding: 'stemCspCapa',
            header: '[t/hr]',
            width: 85,
            align: 'right',
            cellTemplate: (params) => Number(params.value || 0).toLocaleString(),
          },
        ],
      },
      {
        header: String(t('ut.label.NG', 'NG')),
        columns: [
          {
            binding: 'lngCspCapa',
            header: '[Nm3/hr]',
            width: 95,
            align: 'right',
            cellTemplate: (params) => Number(params.value || 0).toLocaleString(),
          },
        ],
      },
      {
        header: String(t('ut.label.Retrun Air', 'Retrun air')),
        columns: [
          {
            binding: 'insdExhaCspCapa',
            header: '[CMH]',
            width: 95,
            align: 'right',
            cellTemplate: (params) => Number(params.value || 0).toLocaleString(),
          },
        ],
      },
      {
        header: String(t('ut.label.Exhaust Air', 'Exhaust air')),
        columns: [
          {
            binding: 'otsdExhaCspCapa',
            header: '[CMH]',
            width: 95,
            align: 'right',
            cellTemplate: (params) => Number(params.value || 0).toLocaleString(),
          },
        ],
      },
      // 2026.08.05 CSR E20260727-000228 Vacuum Pump 추가
      {
        header: String(t('ut.label.Vacuum Pump', 'Vacuum Pump')),
        columns: [
          {
            binding: 'vacmPumpCspCapa',
            header: '[CMH]',
            width: 95,
            align: 'right',
            cellTemplate: (params) => Number(params.value || 0).toLocaleString(),
          },
        ],
      },
      {
        header: String(t('ut.label.Supply Air', 'Supply air')),
        columns: [
          {
            binding: 'suarCspCapa',
            header: '[CMH]',
            width: 95,
            align: 'right',
            cellTemplate: (params) => Number(params.value || 0).toLocaleString(),
          },
        ],
      },
      {
        binding: 'dataInsUserNm',
        header: String(t('ut.label.작성자', '작성자')),
        width: 100,
        align: 'center',
      },
      {
        binding: 'dataInsDtm',
        header: String(t('ut.label.작성일자', '작성일자')),
        width: 100,
        align: 'center',
      },
    ];
  }, [rowData]);

  const onInitialized = (grid) => {
    gridRef.current = grid;
    grid.loadedRows.addHandler(() => {
      const items = grid.collectionView ? grid.collectionView.sourceCollection : grid.itemsSource;
      if (!items || !Array.isArray(items)) {
        return;
      }

      items.forEach((item, rowIndex) => {
        grid.columns.forEach((col, colIndex) => {
          const value = grid.getCellData(rowIndex, colIndex, false);
          const content = CommonUtil.safeHtmlContents(value, false);

          grid.setCellData(rowIndex, colIndex, content, true);
        });
      });

      grid.invalidate();
    });
  };

  const fnSearchUtmNm = (params: any) => {
    navigate('/ut/regist-management/review', {
      state: {
        utmId: params.utmId,
        bildPlntCd: params.bildPlntCd,
      },
    });
    close();
  };

  const btnReload = () => {
    setSearchParam({
      prdnProcCds: [],
      eqclIds: [],
      dtalProcCds: [],
      eqpMchId: '',
    });
  };

  return (
    <CustomDialog
      title={t('ut.label.표준설비별 UT Matrix', '표준설비별 UT Matrix')}
      open={open}
      onClose={close}
      size={'xl'}
    >
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{String(t('ut.label.공정', '공정'))}</label>
                <MultiComboBox
                  id="prdnProcCd"
                  options={prdnProcCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={searchParam?.prdnProcCds || []}
                  onChange={(value) => {
                    handleOnChange('prdnProcCd', value.join());
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('ut.label.설비군', '설비군'))}</label>
                <MultiComboBox
                  id="eqclId"
                  options={eqclIdCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={searchParam?.eqclIds || []}
                  onChange={(value) => {
                    handleOnChange('eqclId', value.join());
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('ut.label.세부공정', '세부공정'))}</label>
                <MultiComboBox
                  id="dtalProcCode"
                  options={dtalProcCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={searchParam?.dtalProcCds || []}
                  onChange={(value) => {
                    handleOnChange('dtalProcCd', value.join());
                  }}
                />
              </SearchCols>
            </SearchRows>
            <SearchRows>
              <SearchCols>
                <label>{t('com.label.Machine', 'Machine')}</label>
                <CustomInputWithSearch
                  name="eqpMchId"
                  value={searchParam.eqpMchId || ''}
                  defaultValue={searchParam.eqpMchId || ''}
                  placeholder={String(
                    t('com.label.Machine을 입력해 주세요.', 'Machine을 입력해 주세요.')
                  )}
                  onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('ut.label.표준설비코드', '표준설비코드')}</label>
                <CustomInputWithSearch
                  name="stndEqpId"
                  value={searchParam.stndEqpId || ''}
                  defaultValue={searchParam.stndEqpId || ''}
                  placeholder={String(
                    t('com.label.표준설비코드를 입력해 주세요.', '표준설비코드를 입력해 주세요.')
                  )}
                  onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <div style={{ display: 'inline-flex', width: '200px', paddingTop: '5px' }}>
                  <label style={{ paddingTop: '0px' }}>
                    {t('ut.label.이전버전보기', '이전버전보기')}
                  </label>
                  <Checkbox
                    id="showPrevVersion"
                    name="showPrevVersion"
                    css={st.checkbox}
                    className="custom"
                    checked={searchParam?.curYn === 'Y'}
                    onClick={(e) => {
                      setSearchParam({
                        ...searchParam,
                        curYn: searchParam?.curYn === 'Y' ? 'N' : 'Y',
                      });
                    }}
                    disableRipple
                  />
                </div>
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <Button
              css={IconButton.button}
              className="reload"
              onClick={btnReload}
              disableRipple
            ></Button>
            <Button css={IconButton.button} className="find" onClick={handleSearch} disableRipple>
              {t('com.button.조회', '조회')}
            </Button>
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('ut.label.표준설비별 UT Matrix List', '표준설비별 UT Matrix List')}</h3>
          <span className="total">
            {t('com.label.총', '총')} <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleExportExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => handleSearch()}
            disableRipple
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={450}
        isReadOnly={true}
        isFilter={false}
        isSelector={false}
        allowPinning={false}
        initialized={onInitialized}
      />
    </CustomDialog>
  );
};

export default UtMatrixRegistHistoryPoupup;
